import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { BriefcaseIcon, StackIcon, SealCheckIcon, HashIcon, UserIcon, InfoIcon } from "@phosphor-icons/react";

export const ProjectsDialog = ({ open, onOpenChange, onLoad }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setItems(data);
    } catch { toast.error("Failed to load projects"); }
    setLoading(false);
  };

  useEffect(() => { if (open) load(); }, [open]);

  const openLatest = async (p) => {
    if (!p.latest_gen_id) { toast.error("This project has no build yet"); return; }
    try {
      const { data } = await api.get(`/history/${p.latest_gen_id}`);
      onLoad(data);
      onOpenChange(false);
    } catch { toast.error("Failed to load configuration"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="projects-dialog" className="max-w-2xl bg-zinc-950 border-zinc-800 text-zinc-200">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <BriefcaseIcon className="h-5 w-5 text-cyan-400" /> My projects
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
          <div data-testid="psdoc-hint" className="flex items-start gap-2 rounded-sm border border-cyan-500/30 bg-cyan-500/[0.07] px-3 py-2 text-[11px] leading-relaxed text-cyan-200/90">
            <InfoIcon weight="fill" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-400" />
            <span>Keep each project's <span className="font-semibold text-cyan-300">PS code</span> handy — you'll need to enter the same code in the <span className="font-semibold">PSDoc tool</span> later to link this project's documentation.</span>
          </div>
          {loading && <p className="text-sm text-zinc-500 py-8 text-center">Loading…</p>}
          {!loading && items.length === 0 && (
            <p className="text-sm text-zinc-600 py-8 text-center">No projects yet. Start a “New design” to create one.</p>
          )}
          {items.map((p) => (
            <div
              key={p.id}
              data-testid={`project-item-${p.id}`}
              onClick={() => openLatest(p)}
              className="group cursor-pointer rounded-sm border border-zinc-800 bg-zinc-900/50 hover:bg-zinc-900 hover:border-zinc-700 px-4 py-3 transition-colors duration-150"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-zinc-100">{p.name}</span>
                    {p.project_code && (
                      <span data-testid={`project-code-${p.id}`} className="inline-flex items-center gap-1 rounded-sm border border-cyan-500/40 bg-cyan-500/10 px-1.5 py-0.5 font-mono text-[10px] text-cyan-300">
                        <HashIcon className="h-3 w-3" />{p.project_code}
                      </span>
                    )}
                    {p.latest_version && (
                      <span className="rounded-sm border border-zinc-700 px-1.5 py-0.5 font-mono text-[10px] text-zinc-400">v{p.latest_version}</span>
                    )}
                    {p.signed_off && (
                      <span className="inline-flex items-center gap-1 rounded-sm border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-300">
                        <SealCheckIcon weight="fill" className="h-3 w-3" /> signed off
                      </span>
                    )}
                  </div>
                  {p.customer && (
                    <p className="flex items-center gap-1 text-xs text-zinc-400"><UserIcon className="h-3 w-3 text-zinc-600" /> {p.customer}</p>
                  )}
                  {p.description && <p className="mt-0.5 text-xs text-zinc-500 truncate">{p.description}</p>}
                  <div className="mt-1 flex items-center gap-2 text-[11px] text-zinc-600">
                    {(p.vendors || []).map((v) => (
                      <span key={v} className="uppercase">{v}</span>
                    ))}
                    <span className="inline-flex items-center gap-1"><StackIcon className="h-3 w-3" /> {p.device_count} device(s)</span>
                    <span>· {p.generations} build(s)</span>
                    <span>· {p.updated_at ? new Date(p.updated_at).toLocaleDateString() : ""}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
